import React, { useState, useEffect } from 'react';
import {
    Box,
    VStack,
    HStack,
    Text,
    Textarea,
    Button,
    Avatar,
    Spinner,
    Flex,
    IconButton,
    Badge,
    Popover,
    Portal,
    Separator
} from '@chakra-ui/react';
import { FiSend, FiChevronLeft, FiChevronRight, FiMessageSquare, FiSmile } from 'react-icons/fi';
import EmojiPicker from 'emoji-picker-react';
import { dataService } from '../services/dataService';
import { useAuth } from '../context/AuthContext';

const itemsPerPage = 3;

const AgentComments = ({ agentId }) => {
    const { user } = useAuth();
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        if (agentId) {
            loadComments();
        }
    }, [agentId]);

    const loadComments = async () => {
        try {
            setLoading(true);
            const response = await dataService.getComments(agentId);
            setComments(response.data);
            setError(null);
        } catch (err) {
            console.error('Error loading comments:', err);
            setError('Failed to load comments');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!newComment.trim()) return;

        try {
            setSubmitting(true);
            const response = await dataService.addComment(agentId, newComment);

            // Add the new comment to the list (top) and reset page to 1
            setComments(prev => [response.data, ...prev]);
            setNewComment('');
            setCurrentPage(1);
        } catch (err) {
            console.error('Error submitting comment:', err);
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return '';
        }
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map(part => part[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // Pagination logic
    const totalPages = Math.ceil(comments.length / itemsPerPage);
    const currentComments = comments.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage(prev => prev - 1);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
    };


    const handleReaction = async (commentId, emoji) => {
        try {
            // Optimistic update
            setComments(prevComments => prevComments.map(c => {
                if (c.id !== commentId) return c;

                const existingReactionIndex = c.reactions.findIndex(r => r.reaction === emoji);
                let newReactions = [...c.reactions];

                if (existingReactionIndex > -1) {
                    const existingReaction = newReactions[existingReactionIndex];
                    if (existingReaction.userReacted) {
                        // Remove reaction
                        if (existingReaction.count === 1) {
                            newReactions.splice(existingReactionIndex, 1);
                        } else {
                            newReactions[existingReactionIndex] = { ...existingReaction, count: existingReaction.count - 1, userReacted: false };
                        }
                    } else {
                        // Add reaction (increment)
                        newReactions[existingReactionIndex] = { ...existingReaction, count: existingReaction.count + 1, userReacted: true };
                    }
                } else {
                    // Add new reaction type
                    newReactions.push({ reaction: emoji, count: 1, userReacted: true });
                }
                return { ...c, reactions: newReactions };
            }));

            await dataService.toggleCommentReaction(commentId, emoji);
            // In a real app, you might want to re-fetch to ensure consistency, but optimistic is fine for now
        } catch (err) {
            console.error('Error toggling reaction:', err);
            // Revert changes if needed (omitted for brevity)
        }
    };

    return (
        <Box h="full" display="flex" flexDirection="column">
            <Flex align="center" gap={3} mb={4}>
                <Box p={2} bg="purple.50" borderRadius="md">
                    <FiMessageSquare color="purple" size={20} />
                </Box>
                <Box>
                    <Text fontSize="md" fontWeight="bold" color="gray.800">
                        Comments & Activity
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                        {comments.length} comment{comments.length !== 1 ? 's' : ''}
                    </Text>
                </Box>
            </Flex>

            {/* Main Content Area */}
            <VStack spacing={6} align="stretch">

                {/* 1. Comments List (Top) */}
                <Box minH="200px">
                    {loading ? (
                        <Flex justify="center" py={10}>
                            <Spinner size="md" color="purple.500" thickness="3px" />
                        </Flex>
                    ) : error ? (
                        <Flex justify="center" py={8} bg="red.50" borderRadius="md">
                            <Text color="red.500" fontSize="sm">{error}</Text>
                        </Flex>
                    ) : comments.length === 0 ? (
                        <Flex
                            direction="column"
                            align="center"
                            justify="center"
                            py={10}
                            bg="gray.50"
                            borderRadius="lg"
                            border="1px dashed"
                            borderColor="gray.300"
                        >
                            <Text color="gray.500" fontSize="sm" fontWeight="medium">
                                No comments yet
                            </Text>
                            <Text color="gray.400" fontSize="xs" mt={1}>
                                Start the conversation by adding a note below
                            </Text>
                        </Flex>
                    ) : (
                        <VStack align="stretch" spacing={4}>
                            {currentComments.map((comment) => (
                                <Flex key={comment.id} gap={4} align="flex-start">
                                    <Avatar.Root size="md" name={comment.display_name} colorPalette="purple">
                                        <Avatar.Fallback fontSize="xs" fontWeight="bold">
                                            {getInitials(comment.display_name)}
                                        </Avatar.Fallback>
                                    </Avatar.Root>
                                    <Box flex="1">
                                        <Box
                                            bg="white"
                                            p={4}
                                            borderRadius="xl"
                                            borderTopLeftRadius="none"
                                            border="1px"
                                            borderColor="gray.100"
                                            shadow="sm"
                                            _hover={{ shadow: 'md', borderColor: 'purple.100' }}
                                            transition="all 0.2s"
                                        >
                                            <Flex justify="space-between" align="center" mb={2}>
                                                <HStack spacing={2}>
                                                    <Text fontWeight="bold" fontSize="sm" color="gray.800">
                                                        {comment.display_name}
                                                    </Text>
                                                    {/* Optional: Add badge for user role if available, or 'You' tag */}
                                                    {user && (user.email === comment.email) && (
                                                        <Badge size="sm" colorPalette="gray" variant="subtle">You</Badge>
                                                    )}
                                                </HStack>
                                                <Text fontSize="xs" color="gray.400">
                                                    {formatDate(comment.created_at)}
                                                </Text>
                                            </Flex>
                                            <Text fontSize="sm" color="gray.700" lineHeight="relaxed" whiteSpace="pre-wrap" wordBreak="break-word">
                                                {comment.comment}
                                            </Text>

                                            {/* Reactions Section */}
                                            <Flex mt={2} gap={2} align="center" wrap="wrap">
                                                {/* Existing Reactions */}
                                                {comment.reactions && comment.reactions.map((r, idx) => (
                                                    <Button
                                                        key={`${r.reaction}-${idx}`}
                                                        size="xs"
                                                        variant="outline"
                                                        bg="white"
                                                        borderColor={r.userReacted ? "purple.500" : "gray.200"}
                                                        color={r.userReacted ? "purple.600" : "gray.600"}
                                                        borderRadius="full"
                                                        px={2}
                                                        h="24px"
                                                        fontSize="xs"
                                                        onClick={() => handleReaction(comment.id, r.reaction)}
                                                        _hover={{ transform: 'scale(1.05)', borderColor: 'purple.400', bg: 'purple.50' }}
                                                    >
                                                        <HStack gap={1}>
                                                            <Text>{r.reaction}</Text>
                                                            <Text fontWeight={r.userReacted ? "bold" : "normal"}>{r.count}</Text>
                                                        </HStack>
                                                    </Button>

                                                ))}

                                                {/* Add Reaction Button */}
                                                <Popover.Root positioning={{ placement: 'top-start' }}>
                                                    <Popover.Trigger asChild>
                                                        <IconButton
                                                            size="xs"
                                                            variant="ghost"
                                                            color="gray.400"
                                                            borderRadius="full"
                                                            aria-label="Add reaction"
                                                            _hover={{ bg: 'gray.100', color: 'purple.500' }}
                                                        >
                                                            <FiSmile size={14} />
                                                            <Text fontSize="xs" ml={1}>+</Text>
                                                        </IconButton>
                                                    </Popover.Trigger>
                                                    <Portal>
                                                        <Popover.Positioner>
                                                            <Popover.Content width="auto" p={2} borderRadius="xl" shadow="xl" bg="white">
                                                                <VStack spacing={2}>
                                                                    {/* Quick Reactions */}
                                                                    <HStack>
                                                                        {['👍', '❤️', '😂', '😡'].map(emoji => (
                                                                            <IconButton
                                                                                key={emoji}
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                fontSize="lg"
                                                                                onClick={() => handleReaction(comment.id, emoji)}
                                                                                aria-label={emoji}
                                                                                _hover={{ bg: 'purple.50', transform: 'scale(1.2)' }}
                                                                            >
                                                                                {emoji}
                                                                            </IconButton>
                                                                        ))}
                                                                    </HStack>
                                                                    {/* Full Picker Trigger could go here if needed, but for now simple list + direct picker */}
                                                                    <Separator />
                                                                    <EmojiPicker
                                                                        onEmojiClick={(emojiData) => handleReaction(comment.id, emojiData.emoji)}
                                                                        width="250px"
                                                                        height="300px"
                                                                        searchDisabled={true}
                                                                        previewConfig={{ showPreview: false }}
                                                                        skinTonesDisabled={true}
                                                                    />
                                                                </VStack>
                                                            </Popover.Content>
                                                        </Popover.Positioner>
                                                    </Portal>
                                                </Popover.Root>
                                            </Flex>
                                        </Box>
                                    </Box>
                                </Flex>
                            ))}
                        </VStack>
                    )
                    }
                </Box >

                {/* Pagination Controls */}
                {
                    comments.length > itemsPerPage && (
                        <Flex justify="center" align="center" gap={4} pt={2}>
                            <Button
                                size="xs"
                                variant="surface"
                                colorPalette="purple"
                                onClick={handlePrevPage}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </Button>
                            <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                Page {currentPage} of {totalPages}
                            </Text>
                            <Button
                                size="xs"
                                variant="surface"
                                colorPalette="purple"
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </Button>
                        </Flex>
                    )
                }

                {/* 2. Add Comment Input (Bottom) */}
                <Box bg="purple.50" p={4} borderRadius="xl" border="1px" borderColor="purple.100">
                    <VStack align="stretch" spacing={3}>
                        <Box position="relative">
                            <Textarea
                                placeholder="Type your comment here..."
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                bg="white"
                                border="1px"
                                borderColor="purple.200"
                                borderRadius="md"
                                rows={3}
                                resize="none"
                                pr="40px" // Make room for emoji button
                                _focus={{ borderColor: 'purple.500', boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)' }}
                                fontSize="sm"
                            />
                            <Popover.Root positioning={{ placement: 'top-end' }}>
                                <Popover.Trigger asChild>
                                    <IconButton
                                        aria-label="Add emoji"
                                        variant="ghost"
                                        size="sm"
                                        position="absolute"
                                        bottom="8px"
                                        right="8px"
                                        color="gray.400"
                                        _hover={{ color: 'purple.500', bg: 'transparent' }}
                                    >
                                        <FiSmile size={20} />
                                    </IconButton>
                                </Popover.Trigger>
                                <Portal>
                                    <Popover.Positioner>
                                        <Popover.Content width="auto" p={0} borderRadius="xl" shadow="xl">
                                            <EmojiPicker
                                                onEmojiClick={(emojiData) => {
                                                    setNewComment((prev) => prev + emojiData.emoji);
                                                }}
                                                width="300px"
                                                height="350px"
                                                previewConfig={{ showPreview: false }}
                                            />
                                        </Popover.Content>
                                    </Popover.Positioner>
                                </Portal>
                            </Popover.Root>
                        </Box>
                        <Flex justify="flex-end">
                            <Button
                                size="sm"
                                colorPalette="purple"
                                onClick={handleSubmit}
                                loading={submitting}
                                disabled={!newComment.trim() || submitting}
                                px={6}
                                borderRadius="full"
                                shadow="sm"
                            >
                                <HStack spacing={2}>
                                    <FiSend />
                                    <Text fontWeight="bold">Post Comment</Text>
                                </HStack>
                            </Button>
                        </Flex>
                    </VStack>
                </Box>

            </VStack >
        </Box >
    );
};

export default AgentComments;
